import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { dashboardApi } from "@/lib/api";
import OnboardingGuide from "@/components/OnboardingGuide";
import { useSettings } from "@/contexts/SettingsContext";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  ShoppingCart, 
  RotateCcw, 
  DollarSign, 
  Package,
  AlertTriangle,
  Clock,
  TrendingUp,
  Users,
  Loader2,
  Wrench,
  Calendar,
  ArrowUp,
  ArrowDown,
  Trophy,
  Zap,
  AlertCircle,
  ChevronRight,
  CalendarRange,
  X,
  Search,
  Scan,
  ArrowLeftRight,
  User,
  Phone,
  CreditCard,
  Banknote,
  CheckCircle,
  Printer,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { darkMode, t } = useSettings();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [returnsControl, setReturnsControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("week");
  const [selectedDay, setSelectedDay] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [customDateActive, setCustomDateActive] = useState(false);
  const navigate = useNavigate();

  // ── Financial Alerts ────────────────────────────────────────────────────
  const [financialAlerts, setFinancialAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  // ============ GLOBAL LOOKUP STATE ============
  const [searchCode, setSearchCode] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const searchInputRef = useRef(null);

  // ============ QUICK MANAGEMENT MODAL STATE ============
  const [quickModal, setQuickModal] = useState(false);
  const [quickRental, setQuickRental] = useState(null);
  const [quickItem, setQuickItem] = useState(null); // Item that triggered the lookup
  const [quickNewBarcode, setQuickNewBarcode] = useState("");
  const [quickNewItem, setQuickNewItem] = useState(null);
  const [quickSwapOldItem, setQuickSwapOldItem] = useState(null);
  const [quickDelta, setQuickDelta] = useState(null);
  const [quickNewDays, setQuickNewDays] = useState("");
  const [quickPaymentMethod, setQuickPaymentMethod] = useState("cash");
  const [quickProcessing, setQuickProcessing] = useState(false);
  const [quickComplete, setQuickComplete] = useState(false);
  const [quickAction, setQuickAction] = useState("swap"); // "swap" or "return"
  const quickSwapInputRef = useRef(null);

  useEffect(() => {
    loadDashboard();
    loadReturnsControl();
    loadFinancialAlerts();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [analyticsPeriod]);

  const loadDashboard = async () => {
    try {
      const response = await dashboardApi.get();
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/financial-alerts`);
      setFinancialAlerts(res.data);
    } catch {
      setFinancialAlerts(null);
    } finally {
      setAlertsLoading(false);
    }
  };

  const loadReturnsControl = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/returns-control`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setReturnsControl(response.data);
    } catch (error) {
      console.error("Error loading returns control:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const params = {};
      
      if (customDateActive && dateRange?.from && dateRange?.to) {
        // Custom date range mode
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      } else {
        // Predefined period mode
        params.period = analyticsPeriod;
      }
      
      const response = await axios.get(`${API}/dashboard/analytics`, {
        params,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    }
  };

  const applyCustomDateRange = () => {
    if (dateRange?.from && dateRange?.to) {
      setCustomDateActive(true);
      loadAnalytics();
      toast.success(`Filtro aplicado: ${format(dateRange.from, 'dd/MM/yyyy', {locale: es})} - ${format(dateRange.to, 'dd/MM/yyyy', {locale: es})}`);
    } else {
      toast.error("Selecciona un rango de fechas válido");
    }
  };

  const clearCustomDateRange = () => {
    setCustomDateActive(false);
    setDateRange(null);
    loadAnalytics();
    toast.info("Filtro de fecha eliminado");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const inventory = stats?.inventory || {};
  const occupancy = data?.occupancy_by_category || {};
  const alerts = data?.alerts || [];

  const maintenanceAlerts = alerts.filter(a => a.type === 'maintenance');
  const overdueAlerts = alerts.filter(a => a.type === 'overdue');

  const categoryColors = {
    SUPERIOR: { bg: 'bg-purple-500/20', bar: 'bg-purple-600', text: 'text-purple-700', light: 'bg-purple-500/10' },
    ALTA: { bg: 'bg-blue-500/20', bar: 'bg-blue-600', text: 'text-blue-700', light: 'bg-blue-500/10' },
    MEDIA: { bg: 'bg-cyan-500/20', bar: 'bg-cyan-600', text: 'text-cyan-700', light: 'bg-cyan-500/10' }
  };

  const getAvailabilityColor = (status) => {
    switch(status) {
      case 'high': return 'bg-cyan-500';
      case 'medium': return 'bg-purple-500';
      case 'low': return 'bg-pink-500';
      default: return 'bg-slate-300';
    }
  };

  const getAvailabilityBg = (status) => {
    switch(status) {
      case 'high': return 'bg-cyan-500/10 border-cyan-200';
      case 'medium': return 'bg-purple-500/10 border-purple-200';
      case 'low': return 'bg-pink-500/10 border-pink-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  const periodLabels = {
    today: t('dashboard.today'),
    week: t('dashboard.thisWeek'),
    month: t('dashboard.thisMonth')
  };

  return (
    <div className={`p-6 lg:p-8 space-y-8 min-h-screen transition-colors duration-300 ${darkMode ? 'bg-[#121212]' : 'bg-slate-50'}`} data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10">
        <div>
          <h1 className={`text-4xl md:text-5xl font-bold mb-2 ${darkMode ? 'text-white' : 'gradient-text'}`} style={{ fontFamily: 'Plus Jakarta Sans', letterSpacing: '-0.02em' }}>
            {t('dashboard.title')}
          </h1>
          <p className={`text-base md:text-lg font-medium ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
            {t('dashboard.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* BOTÓN GRANDE - NUEVO ALQUILER */}
          <Button 
            size="lg"
            className="btn-gradient font-bold px-8 py-6 text-base cursor-pointer shadow-xl hover:shadow-2xl transition-all rounded-xl"
            onClick={() => navigate("/nuevo-alquiler")}
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            {t('dashboard.newRental')}
          </Button>
        </div>
      </div>

      {/* Onboarding guide for new stores */}
      <OnboardingGuide onDashboard />

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
        {/* Card 1: Alquileres Activos */}
        <Card 
          className="relative overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 bg-white border border-slate-200"
          onClick={() => navigate('/alquileres-activos')}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
          
          <CardContent className="p-3 relative">
            <div className="flex items-start justify-between mb-2">
              <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <ShoppingCart className="h-4 w-4 text-blue-600" />
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">{t('dashboard.activeRentals')}</p>
              <p className="text-2xl font-black text-slate-900 mb-0.5">{stats.active_rentals || 0}</p>
              <p className="text-[10px] text-slate-600">{t('dashboard.activeRentals.desc')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Devoluciones Pendientes */}
        <Card 
          className="relative overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 bg-white border border-slate-200"
          onClick={() => navigate('/devoluciones')}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
          
          <CardContent className="p-3 relative">
            <div className="flex items-start justify-between mb-2">
              <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <RotateCcw className="h-4 w-4 text-purple-600" />
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-purple-500 group-hover:translate-x-1 transition-all" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">{t('dashboard.pendingReturns')}</p>
              <p className="text-2xl font-black text-slate-900 mb-0.5">{stats.pending_returns || 0}</p>
              <div className="flex items-center gap-2 mt-0.5">
                {stats.overdue_returns > 0 && (
                  <Badge variant="destructive" className="text-[10px] font-semibold px-1 py-0">
                    <AlertTriangle className="h-2 w-2 mr-0.5" />
                    {stats.overdue_returns}
                  </Badge>
                )}
                <p className="text-[10px] text-slate-600">
                  {stats.overdue_returns === 0 && stats.pending_returns > 0 
                    ? t('dashboard.pendingReturns.allToday')
                    : stats.pending_returns === 0 
                    ? t('dashboard.pendingReturns.none')
                    : t('dashboard.pendingReturns.todayAndOverdue')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Ocupación Stock */}
        <Card 
          className="relative overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 bg-white border border-slate-200"
          onClick={() => navigate('/inventario')}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          
          <CardContent className="p-3 relative">
            <div className="flex items-start justify-between mb-2">
              <div className="h-8 w-8 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Package className="h-4 w-4 text-cyan-600" />
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">{t('dashboard.stockOccupation')}</p>
              <p className="text-2xl font-black text-slate-900 mb-0.5">{inventory.occupancy_percent || 0}%</p>
              <p className="text-[10px] text-slate-600">{t('dashboard.rentedMaterial')}</p>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Clientes del Día */}
        <Card 
          className="relative overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 bg-white border border-slate-200"
          onClick={() => navigate('/clientes')}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
          
          <CardContent className="p-3 relative">
            <div className="flex items-start justify-between mb-2">
              <div className="h-8 w-8 rounded-lg bg-indigo-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="h-4 w-4 text-indigo-600" />
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-0.5">{t('dashboard.dailyCustomers')}</p>
              <p className="text-2xl font-black text-slate-900 mb-0.5">{stats.customers_today ?? 0}</p>
              <p className="text-[10px] text-slate-600">{t('dashboard.uniqueCustomers')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============ ALERTAS FINANCIERAS ============ */}
      {financialAlerts && financialAlerts.count > 0 && (
        <Card
          className={`border-2 ${financialAlerts.alerts.some(a => a.severity === 'critical') ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
          data-testid="financial-alerts-widget"
        >
          <div
            className="flex items-center justify-between px-5 py-3 cursor-pointer"
            onClick={() => setAlertsExpanded(v => !v)}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className={`h-5 w-5 ${financialAlerts.alerts.some(a => a.severity === 'critical') ? 'text-red-600' : 'text-amber-600'}`} />
              <span className="font-bold text-sm">
                {financialAlerts.count} Alerta{financialAlerts.count !== 1 ? 's' : ''} Financiera{financialAlerts.count !== 1 ? 's' : ''}
              </span>
              {financialAlerts.alerts.filter(a => a.severity === 'critical').length > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] h-4">
                  {financialAlerts.alerts.filter(a => a.severity === 'critical').length} CRÍTICA{financialAlerts.alerts.filter(a => a.severity === 'critical').length > 1 ? 'S' : ''}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={(e) => { e.stopPropagation(); loadFinancialAlerts(); }}>
                <Loader2 className={`h-3 w-3 mr-1 ${alertsLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              <ChevronRight className={`h-4 w-4 text-slate-600 transition-transform ${alertsExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>
          {alertsExpanded && (
            <CardContent className="pt-0 pb-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {financialAlerts.alerts.map((alert, i) => (
                  <div
                    key={`alert-${alert.rental_id}-${i}`}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                      alert.severity === 'critical' ? 'bg-red-100 border border-red-200' :
                      alert.severity === 'warning' ? 'bg-amber-100 border border-amber-200' :
                      'bg-blue-50 border border-blue-200'
                    }`}
                    data-testid={`financial-alert-${alert.type}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${
                        alert.severity === 'critical' ? 'text-red-600' :
                        alert.severity === 'warning' ? 'text-amber-600' : 'text-blue-500'
                      }`} />
                      <div className="min-w-0">
                        <span className="font-semibold truncate block">{alert.customer_name}</span>
                        <span className="text-xs text-slate-500 truncate block">{alert.message}</span>
                      </div>
                    </div>
                    {alert.amount > 0.01 && (
                      <span className={`text-sm font-bold ml-3 shrink-0 ${alert.severity === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>
                        {formatCurrency(alert.amount)}
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs ml-2 shrink-0"
                      onClick={() => navigate(`/alquileres-activos?rental=${alert.rental_id}`)}
                    >
                      Ver
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ============ CONTROL DE DEVOLUCIONES - TORRE DE CONTROL ============ */}
      {returnsControl && returnsControl.total_pending > 0 && (
        <Card className="card-modern border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 shadow-xl relative z-10" data-testid="returns-control-panel">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-amber-900 font-bold">{t('dashboard.returnsControl')}</span>
                  <p className="text-xs font-normal text-amber-700 mt-0.5">{t('dashboard.returnsControl.desc')}</p>
                </div>
              </CardTitle>
              <div className="flex items-center gap-3">
                {returnsControl.is_past_closing && returnsControl.total_pending > 0 && (
                  <Badge className="bg-red-500 text-white animate-pulse px-3 py-1.5 font-bold">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    ¡{t('dashboard.delay')}!
                  </Badge>
                )}
                <div className={`text-3xl font-bold ${returnsControl.is_past_closing ? 'text-red-600' : 'text-amber-700'}`}>
                  {returnsControl.total_pending}
                  <span className="text-sm font-normal ml-1">{t('dashboard.items')}</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {returnsControl.pending_by_category.map((cat) => (
                <button
                  key={cat.item_type}
                  onClick={() => navigate(`/devoluciones?filter=${cat.item_type}`)}
                  className={`group p-4 rounded-xl border-2 transition-all hover:shadow-md hover:scale-[1.02] ${
                    returnsControl.is_past_closing 
                      ? 'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100' 
                      : 'border-amber-200 bg-white hover:border-amber-400 hover:bg-amber-500/10'
                  }`}
                  data-testid={`return-control-${cat.item_type}`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`text-4xl font-bold mb-2 ${
                      returnsControl.is_past_closing ? 'text-red-600' : 'text-amber-700'
                    }`}>
                      {cat.count}
                    </div>
                    <p className="text-sm font-semibold text-slate-700 group-hover:text-slate-900">
                      {cat.label}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{t('dashboard.toReturn')}</p>
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    {t('dashboard.viewList')}
                    <ChevronRight className="h-3 w-3" />
                  </div>
                </button>
              ))}
            </div>
            
            {returnsControl.is_past_closing && (
              <div className="mt-4 p-3 rounded-lg bg-red-100 border border-red-300 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    Son las {returnsControl.current_hour}:00h {t('dashboard.overdueWarning')}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => navigate("/devoluciones")}
                >
                  {t('dashboard.manageNow')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ WEEKLY AVAILABILITY CALENDAR ============ */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-500" />
              {t('dashboard.weeklyOccupation')}
            </CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-cyan-500"></div>
                {t('dashboard.highAvail')}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-purple-500"></div>
                {t('dashboard.lowAvail')}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-pink-500"></div>
                {t('dashboard.critical')}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {analytics?.weekly_calendar ? (
            <div className="grid grid-cols-7 gap-2">
              {analytics.weekly_calendar.map((day, idx) => (
                <button
                  key={day.date}
                  onClick={() => setSelectedDay(selectedDay === day.date ? null : day.date)}
                  className={`p-3 rounded-xl border-2 transition-all hover:shadow-md ${
                    day.is_today 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : selectedDay === day.date
                      ? 'border-slate-400 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {/* Day Header */}
                  <div className="text-center mb-3">
                    <p className={`text-xs font-medium ${day.is_today ? 'text-blue-600' : 'text-slate-500'}`}>
                      {day.day_name}
                    </p>
                    <p className={`text-xl font-bold ${day.is_today ? 'text-blue-700' : 'text-slate-800'}`}>
                      {day.day_num}
                    </p>
                  </div>
                  
                  {/* Category Bars — width and color are proportional to
                      the LOCAL available/total ratio, so a row with 0
                      available never renders as a full bar. */}
                  <div className="space-y-2">
                    {['SUPERIOR', 'ALTA', 'MEDIA'].map(cat => {
                      const catData = day.categories[cat] || {};
                      const available = Number(catData.available) || 0;
                      const total = Number(catData.total) || 0;
                      const ratio = total > 0 ? available / total : 0;
                      const widthPct = Math.max(0, Math.min(100, ratio * 100));
                      // Colour matches the legend above (Alta / Pocos / Crítico).
                      const barClass = total === 0
                        ? 'bg-slate-300'
                        : ratio > 0.5
                          ? 'bg-cyan-500'
                          : ratio > 0.2
                            ? 'bg-purple-500'
                            : 'bg-pink-500';
                      return (
                        <div key={cat} className="flex items-center gap-1">
                          <span className="text-[10px] font-medium text-slate-600 w-6">
                            {cat.charAt(0)}
                          </span>
                          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${barClass}`}
                              style={{ width: `${widthPct}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-semibold text-slate-600 w-8 text-right">
                            {available}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Deliveries/Returns indicator */}
                  {(day.deliveries > 0 || day.returns > 0) && (
                    <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-slate-100">
                      {day.deliveries > 0 && (
                        <span className="flex items-center text-[10px] text-cyan-600">
                          <ArrowUp className="h-3 w-3" />
                          {day.deliveries}
                        </span>
                      )}
                      {day.returns > 0 && (
                        <span className="flex items-center text-[10px] text-blue-600">
                          <ArrowDown className="h-3 w-3" />
                          {day.returns}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
            </div>
          )}

          {/* Selected Day Details */}
          {selectedDay && analytics?.weekly_calendar && (
            <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
              {(() => {
                const dayData = analytics.weekly_calendar.find(d => d.date === selectedDay);
                if (!dayData) return null;
                
                return (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 rounded-lg bg-white border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">{t('dashboard.deliveries')}</p>
                      <div className="flex items-center justify-center gap-1">
                        <ArrowUp className="h-5 w-5 text-cyan-500" />
                        <span className="text-2xl font-bold text-cyan-600">{dayData.deliveries}</span>
                      </div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">{t('dashboard.returnsLabel')}</p>
                      <div className="flex items-center justify-center gap-1">
                        <ArrowDown className="h-5 w-5 text-blue-500" />
                        <span className="text-2xl font-bold text-blue-600">{dayData.returns}</span>
                      </div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">{t('dashboard.avgAvailability')}</p>
                      <span className="text-2xl font-bold text-slate-700">
                        {Math.round((dayData.categories.SUPERIOR.percentage + dayData.categories.ALTA.percentage + dayData.categories.MEDIA.percentage) / 3)}%
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ============ PERFORMANCE AND RANKINGS ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rankings Panel */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-purple-500" />
                {t('dashboard.inventoryPerformance')}
              </CardTitle>
              
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Custom Date Range Selector */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant={customDateActive ? "default" : "outline"}
                      size="sm"
                      className={customDateActive ? "bg-primary text-white" : ""}
                    >
                      <CalendarRange className="h-4 w-4 mr-2" />
                      {customDateActive && dateRange?.from && dateRange?.to
                        ? `${format(dateRange.from, 'dd/MM/yy', {locale: es})} - ${format(dateRange.to, 'dd/MM/yy', {locale: es})}`
                        : t('dashboard.customRange')
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <div className="p-4 border-b">
                      <p className="font-medium text-sm">{t('dashboard.selectDateRange')}</p>
                      <p className="text-xs text-slate-500 mt-1">{t('dashboard.analyzeHistory')}</p>
                    </div>
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                      locale={es}
                      className="rounded-md"
                    />
                    <div className="p-3 border-t flex gap-2">
                      <Button 
                        onClick={applyCustomDateRange} 
                        size="sm" 
                        className="flex-1"
                        disabled={!dateRange?.from || !dateRange?.to}
                      >
                        {t('dashboard.applyFilter')}
                      </Button>
                      {customDateActive && (
                        <Button 
                          onClick={clearCustomDateRange} 
                          variant="outline" 
                          size="sm"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Predefined Period Tabs - Only show when custom date is not active */}
                {!customDateActive && (
                  <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                    {['today', 'week', 'month'].map(p => (
                      <button
                        key={p}
                        onClick={() => setAnalyticsPeriod(p)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                          analyticsPeriod === p
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        {periodLabels[p]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="rented" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="rented" className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  {t('dashboard.mostRented')}
                </TabsTrigger>
                <TabsTrigger value="revenue" className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  {t('dashboard.mostProfitable')}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="rented">
                {analytics?.top_rented?.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.top_rented.map((item, idx) => (
                      <div 
                        key={item.barcode}
                        className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-gradient-to-br from-purple-500 to-pink-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-gradient-to-br from-purple-600 to-pink-600' : 'bg-slate-300'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{item.brand} {item.model}</p>
                          <p className="text-xs text-slate-500">
                            {item.item_type} • Talla {item.size} • {item.category}
                          </p>
                        </div>
                        <Badge className="bg-blue-600 text-white border-0">
                          {item.rental_count} {t('dashboard.rentalsCount')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>{t('dashboard.noData')}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="revenue">
                {analytics?.top_revenue?.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.top_revenue.map((item, idx) => (
                      <div 
                        key={item.barcode}
                        className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-gradient-to-br from-cyan-500 to-blue-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-gradient-to-br from-cyan-600 to-blue-600' : 'bg-slate-300'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{item.brand} {item.model}</p>
                          <p className="text-xs text-slate-500">
                            {item.item_type} • Talla {item.size} • {item.category}
                          </p>
                        </div>
                        <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0">
                          {formatCurrency(item.total_revenue)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>{t('dashboard.noData')}</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Side Panel - Stale Stock + Alerts */}
        <div className="space-y-6">
          {/* Stale Stock Alert */}
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-orange-800">
                <AlertCircle className="h-5 w-5" />
                {t('dashboard.staleStock')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.stale_stock?.length > 0 ? (
                <div className="space-y-2">
                  {analytics.stale_stock.map(item => (
                    <div key={item.barcode} className="p-3 rounded-lg bg-white border border-orange-200">
                      <p className="font-medium text-slate-900 text-sm">{item.brand} {item.model}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-slate-500">{item.item_type} • {item.size}</p>
                        <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                          +{item.days_idle}d {t('dashboard.idle')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-orange-700 text-center py-4">
                  {t('dashboard.allStockRotating')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Alerts Summary */}
          {(overdueAlerts.length > 0 || maintenanceAlerts.length > 0) && (
            <Card className="border-red-200 bg-gradient-to-br from-red-50 to-rose-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-red-800">
                  <AlertTriangle className="h-5 w-5" />
                  {t('dashboard.urgentAlerts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {overdueAlerts.length > 0 && (
                  <div className="p-3 rounded-lg bg-white border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-red-500" />
                        <span className="font-medium text-red-800">{t('dashboard.expiredRentals')}</span>
                      </div>
                      <Badge className="bg-red-500 text-white">{overdueAlerts.length}</Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 w-full justify-between text-red-700"
                      onClick={() => navigate("/devoluciones")}
                    >
                      {t('dashboard.viewReturns')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                {maintenanceAlerts.length > 0 && (
                  <div className="p-3 rounded-lg bg-white border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-amber-500" />
                        <span className="font-medium text-amber-800">{t('dashboard.maintenance')}</span>
                      </div>
                      <Badge className="bg-amber-500 text-white">
                        {maintenanceAlerts.reduce((sum, a) => sum + a.count, 0)}
                      </Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 w-full justify-between text-amber-700"
                      onClick={() => navigate("/mantenimiento?view=fleet")}
                    >
                      {t('dashboard.viewEquipment')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

    </div>
  );
}
